import type { ID } from '@octopus-do/octopus-project-core'
import { AngleDown } from '@octopus-do/octopus-storybook/icons'
import type React from 'react'
import { useState } from 'react'

import { clsJoin } from '@/utils'
import type { StructureNode } from '../../types'
import css from './styles.module.css'

interface Props {
  node: StructureNode
  selectedId: ID | null
  onSelect: (id: ID) => void
}

export const TreeRow: React.FC<Props> = ({ node, selectedId, onSelect }) => {
  // Panel-local only: collapsing a branch here shortens the list, it never
  // touches node.collapsed on the canvas and never saves the project.
  const [collapsed, setCollapsed] = useState(false)

  const hasChildren = node.children.length > 0

  const handleSelect = () => onSelect(node.id)

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      // The artboard listens for Enter on the document: without this the same
      // press would jump to the page and open it for editing on the canvas.
      event.stopPropagation()
      handleSelect()
    }
  }

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation()
    setCollapsed(!collapsed)
  }

  return (
    <div>
      <div
        className={clsJoin(css.row, selectedId === node.id && css.selected)}
        role="button"
        tabIndex={0}
        onClick={handleSelect}
        onKeyDown={handleKeyDown}
      >
        {hasChildren ? (
          <button
            type="button"
            className={clsJoin(css.chevron, collapsed && css.chevron_collapsed)}
            aria-label={collapsed ? 'Expand' : 'Collapse'}
            onClick={handleToggle}
          >
            <AngleDown />
          </button>
        ) : (
          <span className={css.chevron_placeholder} />
        )}

        <span className={css.title}>{node.title}</span>
      </div>

      {hasChildren && !collapsed && (
        <div className={css.children}>
          {node.children.map((child) => (
            <TreeRow
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  )
}
